/**
 * Terminals and people — the three capacity items as ONE question (M23; ADR-0015 addendum of 2026-10-05).
 *
 * MIRRORS `packages/shared/src/catalog/capacity.ts` in the server repo (`capacityItems`, `isCapacityEntry`,
 * `peoplePerUnit`, `capacityMix`), copied verbatim with the same deviation note as `api/contracts.ts`: the
 * portals do not import `@nacs/shared` yet. When they do, this file becomes a re-export and nothing else
 * changes. `capacity.test.ts` pins the server's own worked examples, so a drift between the two copies fails
 * here before it reaches a customer as two different answers to "what is the cheapest way to get this?".
 *
 * Kumar, 2026-10-05: a client with about 50 employees will install three terminals. Every terminal was an
 * Access Control unit carrying 250 people, so he would have paid for 750 people he will never use. The
 * catalogue now sells a terminal on its own (`extra_terminal`), which makes three items answer two needs:
 *
 *   Access Control   +1 terminal  +250 people     (the base plan; the first terminal is always this)
 *   Extra terminal   +1 terminal                   (more doors, same people)
 *   Extra people                  +250 people      (more people, same doors)
 *
 * and three steppers side by side would let a customer buy the wrong one. So the portal asks for the NEEDS
 * — how many terminals, how many people — and this module answers with the items: `capacityItems` finds
 * the three by what they ADD (never by code), and `capacityMix` picks the cheapest mix. The configurator,
 * signup, the "terminal limit reached" offer and the api's own suggestion all ask it, so they cannot disagree.
 *
 * Framework-free and pure.
 */

/** What `capacityItems` needs of a catalogue row: the api's `CatalogueEntry` and the portals' DTO both fit. */
export interface CapacityEntry {
  code: string;
  kind: string;
  unitKind: string;
  unitSize: number;
  includes: Readonly<Record<string, number>>;
  status: string;
}

/** The three rows of the "Terminals and people" group, each null when the catalogue has none. */
export interface CapacityItems<T extends CapacityEntry = CapacityEntry> {
  /** The base plan: a terminal that includes people (`includes.people`). */
  base: T | null;
  /** An add-on terminal that includes NO people — "Extra terminal". */
  terminal: T | null;
  /** A lot of people with no terminal — "Extra people". */
  people: T | null;
}

/** Does this item add a terminal or people — i.e. does it belong in the "Terminals and people" group? */
export const isCapacityEntry = (e: Pick<CapacityEntry, "unitKind">): boolean =>
  e.unitKind === "terminal" || e.unitKind === "people_lot";

/** The people one unit adds: Access Control's 250 (its `includes`), a lot's own size, nothing otherwise. */
export const peoplePerUnit = (e: Pick<CapacityEntry, "unitKind" | "unitSize" | "includes">): number =>
  (e.includes["people"] ?? 0) + (e.unitKind === "people_lot" ? e.unitSize : 0);

/**
 * The three capacity rows, found by what they ADD rather than by code, so a renamed or re-coded item still
 * lands in its place. Retired and draft rows are found too — an organisation may still hold one — and the
 * caller decides what may be SOLD (`status === "active"` with a price).
 */
export function capacityItems<T extends CapacityEntry>(catalogue: readonly T[]): CapacityItems<T> {
  return {
    base: catalogue.find((c) => c.kind === "base") ?? null,
    terminal:
      catalogue.find((c) => c.kind !== "base" && c.unitKind === "terminal" && peoplePerUnit(c) === 0) ?? null,
    people: catalogue.find((c) => c.unitKind === "people_lot") ?? null,
  };
}

/** One capacity item as `capacityMix` sees it: what one unit adds, and what it costs (null = not on sale). */
export interface MixUnit {
  /** People one unit adds (Access Control 250, Extra people 250, Extra terminal 0). */
  people: number;
  /** One unit for the chosen period, in paise, after the organisation's own price; null = not for sale. */
  unitPaise: number | null;
  /**
   * The most of this item that may be bought (the catalogue's `maxQuantity`, or a trial's maximum at
   * signup); null or absent = no ceiling. A mix needing more is not a mix the api would accept.
   */
  max?: number | null | undefined;
  /**
   * The least of this item that may be bought when it is bought at all (the catalogue's minimum): a base
   * plan is always bought, so at least this many; an add-on is none, or at least this many. Absent = one
   * for the base plan, nothing for an add-on (review finding, 2026-10-05: a base minimum of two answered
   * with one Access Control and an Extra terminal, which the api refuses).
   */
  min?: number | null | undefined;
}

export interface CapacityMixInput {
  /** Terminals needed, at least one (the first terminal is always the base plan). */
  terminals: number;
  /** People needed. */
  people: number;
  base: MixUnit;
  /** Extra terminal; absent or unpriced = the catalogue does not sell one, so every terminal is a base unit. */
  terminal?: MixUnit | null | undefined;
  /** Extra people; absent or unpriced = people come from base units only. */
  peopleLot?: MixUnit | null | undefined;
}

export interface CapacityMix {
  base: number;
  terminal: number;
  peopleLot: number;
  /** What the mix provides — at least what was asked for. */
  terminals: number;
  people: number;
  /** Σ quantity × unit price, paise, for the period the prices were given in. */
  costPaise: number;
}

/**
 * The cheapest way to cover `terminals` and `people` with the three capacity items.
 *
 * Exhaustive over the number of base units `b` (one to whatever covers the people on base units alone),
 * because it is small and the answer must be the cheapest whatever the prices are — including prices that
 * break M23's rule (Extra terminal + Extra people ≥ Access Control), where the honest answer to a customer
 * is "one Access Control and the rest from parts", and the internal portal's price warning is what stops
 * that from happening by accident. With the rule kept, the answer is the closed form of the plan:
 * `b = min(T, ceil(P / 250))`, `e = T − b`, `l = ceil(P / 250) − b`.
 *
 * Ties go to MORE base units: the same money for the plan the product is named after, and fewer lines on
 * the invoice. An item's `min` and `max` are respected — a mix the api would refuse is never the answer. Returns null
 * when nothing covers the needs (no price on the base plan, or every mix is over a ceiling).
 */
export function capacityMix(input: CapacityMixInput): CapacityMix | null {
  const terminals = Math.max(1, Math.ceil(input.terminals));
  const people = Math.max(0, Math.ceil(input.people));
  const B = input.base.unitPaise;
  if (B === null) return null;
  const perBase = Math.max(0, input.base.people);
  const E = input.terminal?.unitPaise ?? null;
  const lot =
    input.peopleLot && input.peopleLot.unitPaise !== null && input.peopleLot.people > 0
      ? input.peopleLot
      : null;

  // Enough base units to cover the people on their own is the most it can ever make sense to buy.
  const minBase = Math.max(1, input.base.min ?? 1);
  const maxBase = Math.max(terminals, perBase > 0 ? Math.ceil(people / perBase) : terminals, minBase);
  /** Within an item's ceiling, when it has one. */
  const fits = (n: number, unit: MixUnit | null | undefined): boolean =>
    n === 0 || unit?.max === null || unit?.max === undefined || n <= unit.max;
  /** An add-on bought at all is bought at its minimum at least — more than the need, never fewer. */
  const atLeast = (n: number, unit: MixUnit | null | undefined): number =>
    n === 0 ? 0 : Math.max(n, unit?.min ?? 0);
  let best: CapacityMix | null = null;
  for (let b = minBase; b <= maxBase; b++) {
    if (!fits(b, input.base)) break; // every larger b is over the base plan's ceiling too
    const e = atLeast(Math.max(0, terminals - b), input.terminal);
    if (e > 0 && E === null) continue; // no Extra terminal on sale: every terminal is a base unit
    if (!fits(e, input.terminal)) continue;
    const short = Math.max(0, people - b * perBase);
    const needed = short === 0 ? 0 : lot ? Math.ceil(short / lot.people) : -1;
    if (needed < 0) continue; // the people cannot be covered without lots, and there are none
    const l = atLeast(needed, lot);
    if (!fits(l, lot)) continue;
    const costPaise = b * B + e * (E ?? 0) + l * (lot?.unitPaise ?? 0);
    const mix: CapacityMix = {
      base: b,
      terminal: e,
      peopleLot: l,
      terminals: b + e,
      people: b * perBase + l * (lot?.people ?? 0),
      costPaise,
    };
    if (best === null || costPaise < best.costPaise || (costPaise === best.costPaise && b > best.base))
      best = mix;
  }
  return best;
}
